import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/utils/services/dialog.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { finalize, map, Observable, Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { BulevartService } from './bulevart.service';
import { AddBulevartImageComponent } from './add-bulevart-image/add-bulevart-image.component';
import { GalerieBulevart } from './bulevart.interface';
import { ViewBulevartImageComponent } from './view-bulevart-image/view-bulevart-image.component';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CategoriesService } from '../bulevart-settings/categories/categories.service';
import { TertiaryCategoriesService } from '../bulevart-settings/tertiary-categories/tertiary-categories.service';
import { SubcategoriesService } from '../bulevart-settings/subcategories/subcategories.service';
import { EditBulevartImageComponent } from './edit-bulevart-image/edit-bulevart-image.component';

@Component({
    selector: 'app-bulevart',
    templateUrl: './bulevart.component.html',
    styleUrls: ['./bulevart.component.scss']
})
export class BulevartComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    activeValue: boolean | -1 = -1;
    publicValue: boolean | -1 = -1;
    categoryId: number = -1;
    subcategoryId: number = -1;
    tertiaryCategoryId: number = -1;

    categorii: Generic[] = [];
    subcategorii$!: Observable<Generic[]>;
    categoriiTertiare$!: Observable<Generic[]>;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    dtLink = this.bulevartService.listDTUrl;

    filterParameter: { name: string; id: number } = {
        name: 'Toate',
        id: -1,
    };

    parameters: { key: string; value: boolean | -1 }[] = [
        { key: '--Alegeți--', value: -1 },
        { key: 'Da', value: true },
        { key: 'Nu', value: false },
    ];

    resetForm() {
        this.activeValue = -1;
        this.publicValue = -1;
        this.categoryId = -1;
        this.subcategoryId = -1;
        this.tertiaryCategoryId = -1;
        this.onCategorieChange(-1);

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    loadCategoriesActive() {
        this.isFilterLoading = true;
        this.categoriesService
            .getListActive()
            .pipe(
                map((results: any) => [this.filterParameter, ...results]),
                finalize(() => (this.isFilterLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.categorii = results;
                },
                error: (error) => {
                    console.error('Error loading categories', error);
                    this.isFilterLoading = false;
                },
            });
    }

    onCategorieChange(categorieId: number): void {
        if (categorieId > -1) {
            this.loadSubcategorii(categorieId);
        } else {
            this.subcategorii$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: 'Toate', id: -1 }]);
                observer.complete();
            });
        }

        this.categoriiTertiare$ = new Observable<Generic[]>((observer) => {
            observer.next([{ name: 'Toate', id: -1 }]);
            observer.complete();
        });

        this.subcategoryId = -1;
        this.tertiaryCategoryId = -1;
    }

    loadSubcategorii(categorieId?: number) {
        this.isFilterLoading = true;
        let subcategoriiObservable$ = categorieId
            ? this.subcategoriesService.getListByCategory(categorieId)
            : this.subcategoriesService.getListActive();

        this.subcategorii$ = subcategoriiObservable$.pipe(
            map((results: any) => [this.filterParameter, ...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        );
    }

    onSubcategorieChange(subcategorieId: number): void {
        if (subcategorieId > -1) {
            this.loadCategoriiTertiare(subcategorieId);
        } else {
            this.categoriiTertiare$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: 'Toate', id: -1 }]);
                observer.complete();
            });
        }

        this.tertiaryCategoryId = -1;
    }

    loadCategoriiTertiare(subcategorieId?: number) {
        this.isFilterLoading = true;
        let tertiareObservable$ = subcategorieId
            ? this.tertiaryCategoriesService.getListBySubcategory(subcategorieId)
            : this.tertiaryCategoriesService.getListActive();

        this.categoriiTertiare$ = tertiareObservable$.pipe(
            map((results: any) => [this.filterParameter, ...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        );
    }

    constructor(
        private dialogService: DialogService,
        private http: HttpClient,
        private dialog: MatDialog,
        private bulevartService: BulevartService,
        private categoriesService: CategoriesService,
        private subcategoriesService: SubcategoriesService,
        private tertiaryCategoriesService: TertiaryCategoriesService,
        public permissionsService: PermissionsService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.loadCategoriesActive();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.active = this.activeValue;
                dataTablesParameters.public = this.publicValue;
                dataTablesParameters.id_category = this.categoryId;
                dataTablesParameters.id_subcategory = this.subcategoryId;
                dataTablesParameters.id_tertiary_category = this.tertiaryCategoryId;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Denumire fișier',
                    data: 'original_filename',
                },
                {
                    title: 'Categorie',
                    data: 'id_category',
                    render: (data: any, type: any, row: any) => {
                        return row.category.title
                    }
                },
                {
                    title: 'Subcategorie',
                    data: 'id_subcategory',
                    render: (data: any, type: any, row: any) => {
                        return row.subcategory.title
                    }
                },
                {
                    title: 'Categorie terțiară',
                    data: 'id_tertiary_category',
                    render: (data: any, type: any, row: any) => {
                        return row.tertiary_category ? row.tertiary_category.title : 'n/a'
                    }
                },
                {
                    title: 'Poziție',
                    data: 'position',
                    sortable: true,
                    render: (data: any, type: any, row: any, met: any) => {
                        return (
                            `<div class="order-section-dt"> 
                                <button type="button" class="action-btn up-order btn btn-sm btn-primary" title="Poziție +1">
                                    <img class="btn-img" src="/assets/icons/down-white.svg" alt="down-order"/>
                                </button>
                                <span class="order">${row.position}</span>
                                <button type="button" class="action-btn down-order btn btn-sm btn-primary" 
                                    ${row.position == 0 ? 'disabled="true"' : ''} title="Poziție -1">
                                    <img class="btn-img" src="/assets/icons/up-white.svg" alt="up-order"/>
                                </button>
                            </div>`
                        );
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                        <button class="action-btn view ms-1" title="Vizualizare imagine">
                            <img class="btn-img" src="../../../../assets/icons/view.svg" alt="view"/>
                        </button>
                        <button class="action-btn edit ms-1" title="Editare">
                            <img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
                        </button>
                        <button class="action-btn delete ms-1" title="Ștergere">
                            <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
                        </button>
                        <button type="button" class="action-btn update_status_active ms-1 ` +
                            (row.is_active == true ? 'action-success' : 'action-danger') +
                            `" title="Schimbare status activ">
                            <img class="btn-img" src="` +
                            (row.is_active == true
                                ? '/assets/icons/check-white.svg'
                                : '/assets/icons/close-white.svg') +
                            `" alt="update_status_active"/>
                        </button>
                        <button type="button" class="action-btn update_status_public ms-1 ` +
                            (row.is_public == true
                                ? 'action-success'
                                : 'action-danger') +
                            `" title="Schimbare status public">
                            <img class="btn-img" src="` +
                            (row.is_public == true
                                ? '/assets/icons/public_white.svg'
                                : '/assets/icons/public_not_white.svg') +
                            `" alt="update_status_public"/>
                        </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewicon = lastTd?.querySelector('.view') as HTMLElement;
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;
                const updateStatusActiveIcon = lastTd?.querySelector(
                    '.update_status_active'
                ) as HTMLElement;
                const updateStatusPublicIcon = lastTd?.querySelector(
                    '.update_status_public'
                ) as HTMLElement;

                const orderTD = (row as HTMLElement).querySelector('td:nth-last-child(2)');
                const upOrderInSection = orderTD?.querySelector(
                    '.up-order'
                ) as HTMLElement;
                const downOrderInSection = orderTD?.querySelector(
                    '.down-order'
                ) as HTMLElement;

                if (viewicon) {
                    viewicon.addEventListener('click', () => {
                        this.openInfoDialog(data);
                    });
                }

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (deleteIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.delete'
                        )
                    ) {
                        deleteIcon.style.display = 'none';
                    }
                    deleteIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.deleteImage(id);
                    });
                }

                if (updateStatusActiveIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.update-status'
                        )
                    ) {
                        updateStatusActiveIcon.style.display = 'none';
                    }
                    updateStatusActiveIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.changeStatusActive(id);
                    });
                }

                if (updateStatusPublicIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.update-status'
                        )
                    ) {
                        updateStatusPublicIcon.style.display = 'none';
                    }
                    updateStatusPublicIcon.addEventListener('click', () => {
                        const { id } = data;
                        this.changeStatusPublic(id);
                    });
                }

                if (upOrderInSection) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.update-order'
                        )
                    ) {
                        upOrderInSection.style.display = 'none';
                    }
                    upOrderInSection.addEventListener('click', () => {
                        const { id } = data;
                        this.updatePositionInSection(id, 1);
                    });
                }

                if (downOrderInSection) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.bulevart.bulevart-gallery.update-order'
                        )
                    ) {
                        downOrderInSection.style.display = 'none';
                    }
                    downOrderInSection.addEventListener('click', () => {
                        const { id } = data;
                        this.updatePositionInSection(id, -1);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
        this.showCard = false;
    }

    updatePositionInSection(id: number, index: number) {
        this.isLoading = true;

        this.bulevartService.updatePosition(id, index).subscribe(
            () => {
                this.rerenderDT();
            },
            (err) => {
                this.rerenderDT();
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }

                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                });
            }
        );
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddBulevartImageComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare adăugată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openInfoDialog(data: GalerieBulevart) {
        const dialogRef = this.dialog.open(ViewBulevartImageComponent, {
            ...this.dialogService.getDialogConfig(),
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            // if (success) {
            //     this.rerenderDT();
            // }
        });
    }

    openEditDialog(data: any) {
        const dialogRef = this.dialog.open(EditBulevartImageComponent, {
            ...this.dialogService.getDialogConfig(),
            data,
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    deleteImage(id: number) {
        Swal.fire({
            title: 'Sunteți sigur?',
            text: 'Nu o să puteți recupera datele odată cu ștergerea lor!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Confirm!',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.bulevartService.deleteImage(id).subscribe(() => {
                    this.rerenderDT();
                    Swal.fire({
                        title: 'Șters!',
                        text: 'Înregistrare ștearsă cu succes.',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                });
            }
        });
    }

    changeStatusActive(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusActiveAction(id);
            }
        });
    }

    changeStatusActiveAction(id: number) {
        this.isLoading = true;
        console.log(id);
        this.bulevartService.changeStatusActive(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err: any) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusActiveAction(id);
                    }
                });
            }
        );
    }

    changeStatusPublic(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O să actualizați statusul înregistrării selectate!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.changeStatusPublicAction(id);
            }
        });
    }

    changeStatusPublicAction(id: number) {
        this.isLoading = true;
        this.bulevartService.changeStatusPublic(id).subscribe(
            () => {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Înregistrare actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.changeStatusPublicAction(id);
                    }
                });
            }
        );
    }
}



